/*
 * NOTICE:  All information contained herein is, and remains the property of Power Settlements Consulting and Software, LLC.
 * The intellectual and technical concepts contained herein are proprietary to Power Settlements Consulting and Software, LLC
 * and are protected by trade secret or copyright law. Dissemination or reproduction of this material, in whole or in part,
 * is strictly forbidden without the prior written permission of Power Settlements Consulting and Software, LLC.
 * Copyright (c) Power Settlements Consulting and Software, LLC. All rights reserved.
*/

import moment from 'moment';
import { getMomentDateString, formatDate } from '@/utils/dateUtil';
import { CISO_SAMC_API } from '@/api';
import {
  schedulingCoordinatorsWithTagbox, tradeDateRange, samcHeaders,
} from '@/components/Ciso/BaseStore/CisoConfigs';
import { formatReadableUSD } from '@/utils/dataUtil';
import { createMutations } from '@/utils/vuexHelper';
import tableConfigs from './invoiceTableConfigurations';

function findFileTypes({ params, $store }) {
  const selectedCoordinator = params.schedulingCoordinatorsWithTagbox.find(({ shortName }) => params.schedulingCoordinatorsWithTagboxSelected === shortName);
  const [startDate, endDate] = params.tradeDateRangeSelected.map((date) => (moment(date).utc().format().split('T')[0]));

  let filteredHeaders = params.samcHeadersUnfiltered;

  filteredHeaders = filteredHeaders.filter(({ publishedDate }) => moment(publishedDate.split('T')[0]).isBetween(startDate, endDate, null, '[]'));
  const filteredFileTypes = [...new Set(filteredHeaders.map(({ fileType }) => fileType))].map((f) => ({ value: f, label: f })).filter((f) => f.label !== 'DAILY_INITIAL_MARKET');

  if (filteredFileTypes.length > 1) { filteredFileTypes.unshift({ value: 'ALL', label: 'ALL' }); }
  const fileTypeSelected = filteredFileTypes.find((x) => x);
  const selectedValue = (fileTypeSelected) ? fileTypeSelected.value : null;

  // Resets the selection options to null when no SC is selected
  if (params.schedulingCoordinatorsWithTagboxSelected.length === 0) {
    $store.commit('ciso/setParams', ['samcHeaders', []]);
    $store.commit('ciso/setParams', ['fileTypes', []]);
    $store.commit('ciso/setParams', ['fileTypesSelected', null]);
  } else {
    $store.commit('ciso/setParams', ['samcHeaders', filteredHeaders]);
    $store.commit('ciso/setParams', ['fileTypes', filteredFileTypes]);
    $store.commit('ciso/setParams', ['fileTypesSelected', selectedValue]);
  }
}

const state = {
  summaryTableConfiguration: tableConfigs.summaryTableConfiguration,
  summaryChildTableConfiguration: tableConfigs.summaryChildTableConfiguration,
  statementTableConfiguration: tableConfigs.statementTableConfiguration,
  tableData: [],
  childTableData: [],
  statementData: [],
  currentTableRow: [],
  headers: [],
  searchFiltersConfig: {
    samcHeaders,
    tradeDateRange: { ...tradeDateRange, label: 'Publish Date' },
    schedulingCoordinatorsWithTagbox,
    fileTypes: {
      label: 'File Type',
      value: null,
      type: 'select',
      visible: true,
      options: [],
      watchers: [{
        propertyToWatch: 'samcHeadersUnfiltered',
        handler(newValue, oldValue) {
          if (!this.params.tradeDateRangeSelected || this.params.tradeDateRangeSelected.length < 0 || !this.params.samcHeadersUnfiltered || this.params.samcHeadersUnfiltered.length < 0 || !this.params.schedulingCoordinatorsWithTagboxUnfiltered || this.params.schedulingCoordinatorsWithTagboxUnfiltered.length < 0) {
            return;
          }
          findFileTypes(this);
        },
      },
      {
        propertyToWatch: 'schedulingCoordinatorsWithTagboxSelected',
        handler(newValue, oldValue) {
          if (newValue === oldValue || !newValue || !this.params.tradeDateRangeSelected || this.params.tradeDateRangeSelected.length < 0 || !this.params.samcHeadersUnfiltered || this.params.samcHeadersUnfiltered.length < 0 || !this.params.schedulingCoordinatorsWithTagboxUnfiltered || this.params.schedulingCoordinatorsWithTagboxUnfiltered.length < 0) {
            return;
          }
          findFileTypes(this);
        },
      }, {
        propertyToWatch: 'tradeDateRangeSelected',
        deep: true,
        async handler(newValue, oldValue) {
          if (newValue && newValue.length > 0) {
            try {
              const paramsObj = { startDate: formatDate(newValue[0], 'YYYY-MM-DD'), endDate: formatDate(newValue[1], 'YYYY-MM-DD') };
              const dataObj = await samcHeaders.fetchData(paramsObj);
              this.params.samcHeadersUnfiltered = dataObj;
            } catch (error) {
              this.$notify('Failed to Load statements', 'error');
              console.log(error);
            }
          }

          if (!newValue || newValue.length < 0 || !this.params.schedulingCoordinatorsWithTagboxSelected || !this.params.samcHeadersUnfiltered || this.params.samcHeadersUnfiltered.length < 0 || !this.params.schedulingCoordinatorsWithTagboxUnfiltered || this.params.schedulingCoordinatorsWithTagboxUnfiltered.length < 0) {
            return;
          }
          findFileTypes(this);
        },
      }],
    },
    fileVersions: {
      label: 'Version',
      value: null,
      type: 'select',
      visible: true,
      options: [],
      watchers: [{
        propertyToWatch: 'fileTypesSelected',
        handler(newValue, oldValue) {
          let filteredHeaders = [];
          if (this.params.fileTypesSelected !== 'ALL') { filteredHeaders = [...new Set(this.params.samcHeaders.filter(({ fileType }) => fileType === newValue).map(({ version }) => (version)))].map((version) => ({ value: version, label: version })); } else { filteredHeaders = [...new Set(this.params.samcHeaders.map(({ version }) => (version)))].map((version) => ({ value: version, label: version })); }
          if (filteredHeaders.length > 1) {
            filteredHeaders.unshift({ value: -1, label: 'ALL' });
          }
          const versionSelected = filteredHeaders.find((x) => x);
          const selectedValue = (versionSelected) ? versionSelected.value : null;
          this.$store.commit('ciso/setParams', ['fileVersions', filteredHeaders]);
          this.$store.commit('ciso/setParams', ['fileVersionsSelected', selectedValue]);
        },
      }],
    },
  },
};

const getters = {
  getChildSummaryData(state) {
    if (state.childTableData.length > 0) {
      const summaryItem = {
        lineAmount: 0,
        previousAmount: 0,
        netAmount: 0,
        summaryFlag: true,
        id: -1,
        parentId: 0,
      };

      state.childTableData.forEach(({
        parentId, lineAmount, previousAmount, netAmount,
      }) => {
        if (parentId === 0) {
          summaryItem.lineAmount += lineAmount;
          summaryItem.previousAmount += previousAmount;
          summaryItem.netAmount += netAmount;
        }
      });
      summaryItem.lineAmount = formatReadableUSD(summaryItem.lineAmount);
      summaryItem.previousAmount = formatReadableUSD(summaryItem.previousAmount);
      summaryItem.netAmount = formatReadableUSD(summaryItem.netAmount);
      return [summaryItem];
    }

    return [];
  },
};

const actions = {
  currentTableRowChange({ commit }, currentRow) {
    commit('setChildTableData', currentRow.details);
    commit('setStatementData', currentRow.statements);
    commit('setCurrentTableRow', currentRow);
  },
  async fetchInvoices({ commit }, {
    schedulingCoordinatorsWithTagboxSelected, tradeDateRangeSelected, fileTypesSelected, fileVersionsSelected,
  }) {
    // const params = { scs: 'SCE1', startDate: '2019-03-22T07:00:00.000Z', endDate: '2019-06-22T07:00:00.000Z', dateType: 'Trade Date', fileType: 'MARKET_INITIAL', version: 5 };
    const params = {
      scs: schedulingCoordinatorsWithTagboxSelected.toString(),
      startDate: tradeDateRangeSelected[0].toISOString(),
      endDate: tradeDateRangeSelected[1].toISOString(),
      fileType: fileTypesSelected,
      version: fileVersionsSelected,
    };
    try {
      const { data: { data } } = await CISO_SAMC_API.get('/invoices?', { params });
      // TODO: Fix this grossness somehow
      if (Array.isArray(data)) {
        data.forEach((d) => {
          d.tradeDate = getMomentDateString(d.tradeDate);
          d.publishedDate = getMomentDateString(d.publishedDate);
          d.dueDate = getMomentDateString(d.dueDate);
          d.invoiceAmount = d.invoiceAmount.toFixed(2);
          d.calcAmount = d.calcAmount.toFixed(2);
          d.variance = d.variance.toFixed(2);

          if (Array.isArray(d.details)) {
            d.details.forEach((detail) => {
              detail.tzStartDate = getMomentDateString(detail.tzStartDate);
              detail.tzEndDate = getMomentDateString(detail.tzEndDate);
              detail.chargeCode = detail.chargeCode === '0' ? null : detail.chargeCode;
            });
          }
          if (Array.isArray(d.statements)) {
            d.statements.forEach((statement) => {
              statement.tzTradeDate = getMomentDateString(statement.tzTradeDate);
            });
          }
        });
        commit('setTableData', data);
        commit('setChildTableData', (data.length > 0 && data[0].details) ? data[0].details : []);
        commit('setStatementData', (data.length > 0 && data[0].statements) ? data[0].statements : []);
      }
    } catch (error) {
      this.$notify('Failed to Load Invoices', 'error');
      console.log(error);
    }
  },
  reset({ commit }) {
    commit('setTableData', []);
    commit('setChildTableData', []);
    commit('setStatementData', []);
  },
};

const mutations = {
  ...createMutations(
    'childTableData',
    'currentTableRow',
    'statementData',
    'tableData',
  ),
  reset(state) {
    state.tableData = [];
    state.childTableData = [];
    state.statementData = [];
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};